// components/PurchasingDepartmentScorecardTabSet.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Text, Anchor, Group } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';

import CalendarMasterDetail from '../../../CalendarMasterDetail/CalendarMasterDetail';
import '@fontsource/orbitron/700.css';

import { GET_DEPARTMENT_CALENDAR_QUERY } from '../../../../PurchasingScorecardQueries.js';

import PurchasingDepartmentPerformancePanel from './PurchasingDepartmentPerformancePanel.js';
import RFQDayDetails from './calendar/RFQDayDetails.js';

import MembershipPopup from '../MembershipPopup.js';
import SQLEditorPopup from '../SQLEditorPopup.js';
import config from '../../../../config.js';
import PODayDetails from './calendar/PODayDetails.js';
import CallTrackerDetails from './calendar/CallTrackerDetails.js';
import PriceUpdateDetails from './calendar/PriceUpdateDetails.js';

const PurchasingDepartmentScorecardTabSet = ({ filter }) => {
  const [activeTab, setActiveTab] = useState('0');
  const [activeCalendarTab, setActiveCalendarTab] = useState(0);
  // State for technical pop-ups
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab');

  // http://localhost:3000/supply-chain/purchasing/department/scorecard/rfq-day-details?date=1/1/2025

  // For department-level queries, we're using a fixed department code
  const departmentCode = "Purchasing";

  // Load department activity data - include filter in variables
  const [loadDepartmentData, { data: departmentData, loading, error }] = useLazyQuery(
    GET_DEPARTMENT_CALENDAR_QUERY,
    {
      variables: { 
        ccode: departmentCode,
        // Convert filter object to the expected string format for the API
        ...(filter && filter.purchasingScorecardOnly !== undefined && { 
          filter: filter.purchasingScorecardOnly ? 'IN' : 'OUT' 
        })
      },
    }
  );

  // Set the initial active tab based on the 'tab' parameter in the URL
  useEffect(() => {
    if (defaultTab) {
      const tabIndex = parseInt(defaultTab, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
        setActiveTab(tabIndex.toString());

        // Load data if the activity tab is selected by default
        if (tabIndex === 1) {
          loadDepartmentData();
        }
      }
    }
  }, [defaultTab, loadDepartmentData]);

  // KEY CHANGE: Add a useEffect to reload data when the tab is active or filter changes
  // This ensures the data is fresh and triggers a re-render
  useEffect(() => {
    if (activeTab === '1') {
      loadDepartmentData();
    }
  }, [activeTab, filter, loadDepartmentData]); // Added filter as dependency

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    // Always load the data when switching to the activity tab
    // Removed the !departmentData condition
    if (newValue === '1') {
      loadDepartmentData();
    }
  };

  // New activities configuration for department-wide data
  const departmentActivitiesConfig = useMemo(() => [
    {
      type: 'RFQs',
      color: '#1976d2',
      dataFields: {
        docCount: 'totalRFQDocCount',  // Use the aggregated RFQ count from your data
        lineCount: 'totalRFQLineCount',
        total: 'totalRFQDollars',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: (props) => <RFQDayDetails {...props} filter={filter} />,
    },
    {
      type: 'POs',
      color: '#2D875D',
      dataFields: {
        docCount: 'poDocCount',
        lineCount: 'poLineCount',
        total: 'poDollars',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: (props) => <PODayDetails {...props} filter={filter} />,
    },
    {
      type: 'Price Updates',
      color: '#A23E48',
      dataFields: {
        // Assuming sellPricesUpdated holds a count or indicator; adjust as needed.
        lineCount: 'sellPricesUpdated',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: (props) => <PriceUpdateDetails {...props} filter={filter} />,
    },
    {
      type: 'Call Trackers',
      color: '#9C4EE5',
      dataFields: {
        lineCount: 'callTrackerDocCount',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: (props) => <CallTrackerDetails {...props} filter={filter} />,
    },
  ], [filter]); // Added filter as dependency

  // Format the department data as needed for CalendarMasterDetail
  const formattedDepartmentData = useMemo(() => {
    if (!departmentData) return [];
    // Add a console log to verify data is being processed
    console.log("Processing department data for calendars:", departmentData);
    return departmentData.departmentCalendarActivity.map((item) => ({
      date: item.date,
      ...item, // Spread all fields so that CalendarMasterDetail can access them via the keys defined in departmentActivitiesConfig
    }));
  }, [departmentData]);

  // KEY CHANGE: Add a key prop that changes when data or filter changes to force re-render
  const calendarKey = `calendar-${departmentData ? departmentData.departmentCalendarActivity.length : 'empty'}-${filter ? JSON.stringify(filter) : 'no-filter'}`;

  return (
    <Box style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="0">Scorecard</Tabs.Tab>
       {/*      <Tabs.Tab value="1">RFQs, POs, Price Updates, Call Trackers</Tabs.Tab> */}
          </Tabs.List>
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box style={{ flexGrow: 1, overflow: 'hidden', padding: 0, height: '100%' }}>
        {activeTab === '0' && (
          <PurchasingDepartmentPerformancePanel filter={filter} />
        )}
        {activeTab === '1' && (
          <>
            {/* Technical options: small links for debugging */}
            <Group mb="sm" gap="sm">
              <Anchor
                size="xs"
                c="dimmed"
                onClick={() => setOpenMembership(true)}
                style={{ cursor: 'pointer' }}
              >
                Who is Included?
              </Anchor>
              <Anchor
                size="xs"
                c="dimmed"
                onClick={() => setOpenSummaryQuery(true)}
                style={{ cursor: 'pointer' }}
              >
                Calendar Summary Query
              </Anchor>
            </Group>

            <CalendarMasterDetail
              key={calendarKey} // KEY CHANGE: Add key prop to force re-render when filter changes
              businessKey={departmentCode}
              data={formattedDepartmentData}
              currentYear={new Date().getFullYear()}
              currentMonth={new Date().getMonth()}
              onMonthChange={(year, month) => {
                console.log(`Month changed to: ${month + 1}/${year}`);
                // KEY CHANGE: Reload data when month changes
                loadDepartmentData();
              }}
              activities={departmentActivitiesConfig}
              filter={filter} // Pass filter to CalendarMasterDetail
            />
          </>
        )}
      </Box>

       {/* Pop-up dialogs */}
            <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
            <SQLEditorPopup
              opened={openSummaryQuery}
              onClose={() => setOpenSummaryQuery(false)}
              title="Summary Query"
              endpoint={`${config.apiBaseUrl}/supply-chain/purchasing/department/scorecard/queries/calendar/calendar-summary-query`}
            />
        
    </Box>
  );
};

export default PurchasingDepartmentScorecardTabSet;